// Assignment Completion by Business Unit report (ADR-093, amended: year-only period)
// One row per terminal BU for a selected year (the current year renders
// year-to-date server-side): Measured individuals,
// Expected (policy), Open (created & unfinished), Completed,
// Missing (never created), and % Completed (completed / EXPECTED).
// Mirrors the Fixed Term Contracts report shell: header stats, filters bar,
// state restore via navigation state (acrState — seeded by the dashboard
// card's deep links).
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaArrowLeft,
  FaSortAmountDown,
  FaSortAmountUp,
  FaExclamationTriangle,
  FaChevronRight,
} from 'react-icons/fa';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, MainSection, BackButton } from '../styles';
import {
  FiltersBar,
  HeaderStats,
  HeaderStat,
  GroupHeader,
  GridHeader,
  SuRow,
  BarCell,
  OpenCell,
  BreakdownRow,
  RateCell,
  DataQualityFooter,
  SortButton,
} from './styles';
import LoadingSpinner from '../../common/LoadingSpinner';
import EmptyState from '../../common/EmptyState';
import ResultsCount from '../../common/ResultsCount';
import ClusterSelector from '../../common/ClusterSelector';
import BusinessUnitSelector from '../../common/BusinessUnitSelector';
import OrgUnitTypeSelector from '../../common/OrgUnitTypeSelector';
import EmployeeSearch from '../../common/EmployeeSearch';
import { EmployeeSearchProvider } from '../../common/EmployeeSearch/contexts/EmployeeSearchProvider';
import EmployeeReportingScope from '../../common/filters/EmployeeReportingScope';
import { EMPLOYEE_REPORTING_SCOPE } from '../../../constants/employeeReportingScope';
import { ORG_UNIT_TYPE_RETURN_TYPES } from '../../../constants/selectorReturnTypes';
import { assignmentCompletionApi } from '../../../services/api/assignmentCompletionApi';
import {
  ASSIGNMENT_KIND_OPTIONS,
  BELOW_RATE_OPTIONS,
  BAR_COLORS,
  getColumnDescriptions,
  describeSkippedBreakdown,
  SKIPPED_REASONS,
} from '../../../constants/assignmentCompletion';
// ADR-098 §1: the panel speaks the SAME status vocabulary as the BU detail
// page's chips. One source, or the two views become two dialects.
import {
  OPEN_STATUS_ORDER,
  ASSIGNMENT_BADGES,
  getStatusText,
  getStatusColor,
  getStatusTextColor,
  getStatusDescription,
  getAssignmentTooltipMyPerspective,
} from '../../../constants/assignmentStatuses';

// The server reports which scope filter hid a row by key; these are the names
// those filters carry in the bar above, so the note points at a control the
// reader can actually see and change.
const FILTER_LABELS = {
  orgUnitType: 'Unit Type',
  cluster: 'Cluster',
  businessUnit: 'Business Unit',
};

const AssignmentCompletionReport = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Entry state: seeded by the dashboard card's deep links (ADR-093), same
  // contract as the FTC report's ftcState.
  const restored = location.state?.acrState;
  const backTo = restored?.backTo || '/reports';
  const backLabel = restored?.backLabel || 'Reports';

  // Current filter state, carried into the BU detail page so its population
  // matches this view and Back restores these filters (ADR-094).
  const buildAcrState = () => ({
    year,
    clusterId,
    businessUnitId,
    orgUnitTypeCode,
    representative,
    representativeScope,
    assignmentKind,
    belowRate,
    orderDirection,
    // ADR-098 §5: which Open panels are open travels with the filters. A view
    // that forgets what you opened punishes exactly the reader who went to
    // check something and came back. Serialized as an array — router state is
    // structured-cloned, and a Set survives, but an array is what the restore
    // path can read without knowing which it got.
    expandedRowIds: [...expandedRowIds],
    backTo,
    backLabel,
  });

  const openServiceUnit = (row) => {
    navigate(`/reports/assignment-completion/service-units/${row.businessUnit.id}`, {
      state: { acrState: buildAcrState() },
    });
  };

  const now = useMemo(() => new Date(), []);

  // Filter state — the period defaults to the current year (year-to-date)
  const [year, setYear] = useState(restored?.year || now.getFullYear());
  const [clusterId, setClusterId] = useState(restored?.clusterId || '');
  const [businessUnitId, setBusinessUnitId] = useState(restored?.businessUnitId || '');
  // Organizational Unit Type (ADR-093 amendment 2): defaults to Delivery,
  // '' means All Types. ?? (not ||) so a restored '' survives.
  const [orgUnitTypeCode, setOrgUnitTypeCode] = useState(restored?.orgUnitTypeCode ?? 'delivery');
  // Representative filter (amendment 4) — the dashboard card deep-links here
  // with itself + Hierarchy, which is what makes its numbers verifiable.
  const [representative, setRepresentative] = useState(restored?.representative || null);
  const [representativeScope, setRepresentativeScope] = useState(
    restored?.representativeScope || EMPLOYEE_REPORTING_SCOPE.DIRECT,
  );
  const [assignmentKind, setAssignmentKind] = useState(restored?.assignmentKind || 'all');
  const [belowRate, setBelowRate] = useState(restored?.belowRate ?? '');
  const [orderDirection, setOrderDirection] = useState(restored?.orderDirection || 'desc');

  // Data state
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // ADR-098: rows whose Open breakdown is showing. Several at once — comparing
  // two business units is the common reason to open one at all.
  const [expandedRowIds, setExpandedRowIds] = useState(
    () => new Set(restored?.expandedRowIds || []),
  );

  const toggleBreakdown = (buId) => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(buId)) {
        next.delete(buId);
      } else {
        next.add(buId);
      }
      return next;
    });
  };

  const yearOptions = useMemo(() => {
    const current = now.getFullYear();
    const years = [];
    for (let y = current; y >= current - 4; y--) {
      years.push(y);
    }
    return years;
  }, [now]);

  const fetchReport = useCallback(async () => {
    try {
      setIsLoading(true);

      const params = {
        year,
        orderBy: 'completionRate',
        orderDirection,
      };

      if (clusterId) {
        params.clusterId = clusterId;
      }
      if (businessUnitId) {
        params.businessUnitId = businessUnitId;
        params.buScope = 'hierarchy';
      }
      if (representative) {
        params.representativeId = representative.id;
        params.reportingScope = representativeScope;
      }
      // Always sent explicitly: the API defaults an ABSENT param to
      // 'delivery', so All Types must be the literal sentinel 'all'.
      params.orgUnitType = orgUnitTypeCode || 'all';
      if (assignmentKind && assignmentKind !== 'all') {
        params.assignmentKind = assignmentKind;
      }
      if (belowRate !== '' && belowRate !== null && belowRate !== undefined) {
        params.belowRate = belowRate;
      }

      const data = await assignmentCompletionApi.getAssignmentCompletionReport(params);
      setReport(data);
    } catch (error) {
      console.error('Error fetching assignment completion report:', error);
    } finally {
      setIsLoading(false);
    }
  }, [year, clusterId, businessUnitId, orgUnitTypeCode, representative, representativeScope, assignmentKind, belowRate, orderDirection]);

  // Clearing the representative resets the scope, so a later selection starts
  // from the Direct Reports default again (ADR-092 behaviour).
  const handleRepresentativeChange = useCallback((emp) => {
    setRepresentative(emp);
    if (!emp) {
      setRepresentativeScope(EMPLOYEE_REPORTING_SCOPE.DIRECT);
    }
  }, []);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const toggleSortDirection = () => {
    setOrderDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const summary = report?.summary || {
    eligibleCount: 0,
    expectedCount: 0,
    completedCount: 0,
    notCompletedCount: 0,
    missingCount: 0,
    surplusCount: 0,
    surplusOverTargetCount: 0,
    surplusUntypedCount: 0,
    skippedCount: 0,
    excludedByEngagementModelCount: 0,
    completionRate: null,
    suCounts: { total: 0, belowThreshold: 0 },
  };
  const rows = report?.rows || [];
  const dataQuality = report?.dataQuality || {
    individualsWithoutHireDate: 0,
    individualsWhoLeft: 0,
    individualsBelowTenure: 0,
    assignmentsOutsidePopulation: 0,
    assignmentsOutsideByReason: {},
    populationScoped: false,
    individualsBelowTenureWithoutRow: 0,
    individualsWithoutPositionAtReference: 0,
    skippedHiddenByFilter: [],
    serviceUnitsWithoutUnitType: 0,
    // ADR-108
    individualsExcludedByEngagementModel: 0,
    excludedByEngagementModel: [],
    skippedWithoutRowByReason: {},
  };

  // Population-level notes. They describe the whole measured population, not
  // the rows left after the cluster / BU / Unit Type filters — the per-BU view of
  // the same exclusions is the Skipped column. The population IS narrowed by
  // the Representative filter though, so the phrasing follows the server's
  // populationScoped flag rather than claiming "organization-wide" inside
  // one manager's span.
  const populationLabel = dataQuality.populationScoped ? 'in this scope' : 'across the organization';
  // Read from the API's echoed policy so the copy states the real threshold.
  const tenureMonths = report?.policy?.minTenureMonths;
  const tenurePhrase = tenureMonths
    ? `less than ${tenureMonths} months`
    : 'not long enough';

  // Each note is a headline count plus the parts that make it up, rendered as
  // its own line with indented sub-lines. Previously every note was a string
  // and they were joined with "·", so a breakdown ("3 of this, 15 of that")
  // sat at the same visual level as an unrelated total and the reader had to
  // work out which numbers belonged to which. Structure carries that now.
  const dataQualityNotes = [];

  // A part that accounts for the whole says "All", not the number again —
  // except when the whole is 1, where "All is …" would read wrong.
  const part = (count, total, phrase) => (count === total && total > 1
    ? `All ${phrase}`
    : `${count} ${phrase}`);
  const be = (count) => (count === 1 ? 'is' : 'are');

  // Sub-items are sentences ABOUT a count, so they have to inflect with it:
  // "1 belongs to someone", "3 belong to people". The headlines already did
  // this; the parts did not, which produced "1 are extra assignments" as soon
  // as a single assignment landed in a bucket — most easily hit with a 0 + 0
  // position override, where one person's entire output becomes surplus.
  const inflect = (count, one, many) => (count === 1 ? one : many);

  // Name the units rather than counting them — with a handful, the names are
  // the answer. Beyond three the list stops being readable, so it truncates.
  const namedUnits = (names = []) => {
    if (names.length === 0) {
      return 'business units not shown here';
    }
    if (names.length <= 3) {
      return names.join(', ');
    }
    return `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`;
  };

  // Where a skipped population went: in the rows above, in a unit that has no
  // row, hidden by a NAMED filter, or elsewhere. Shared by the tenure note
  // and the excluded-model note (ADR-108), each over ITS OWN people, so the
  // two never double-count the Skipped column between them. The server says
  // which filter hid whom, per reason.
  const reconcileSkipped = ({ total, inView, withoutRow, reason }) => {
    const hiddenByFilter = (dataQuality.skippedHiddenByFilter || [])
      .map((entry) => ({
        ...entry,
        count: entry.countByReason ? (entry.countByReason[reason] || 0) : entry.count,
      }))
      .filter((entry) => entry.count > 0);
    const hiddenNamed = hiddenByFilter.reduce((sum, entry) => sum + entry.count, 0);
    const hiddenUnattributed = Math.max(0, total - inView - withoutRow - hiddenNamed);

    return [
      [inView, `${be(inView)} in the rows above (the Skipped column)`],
      [withoutRow, `${be(withoutRow)} the only ${withoutRow === 1 ? 'person' : 'people'} in their business unit, so it has no row here yet`],
      ...hiddenByFilter.map((entry) => [
        entry.count,
        `${be(entry.count)} in ${namedUnits(entry.serviceUnits)}, hidden by the ${FILTER_LABELS[entry.filter] || 'active'} filter`,
      ]),
      [hiddenUnattributed, `${be(hiddenUnattributed)} in business units not shown here`],
    ]
      .filter(([n]) => n > 0)
      .map(([n, phrase]) => part(n, total, phrase));
  };

  if (dataQuality.individualsBelowTenure > 0) {
    // Two numbers for one rule live on this page: this population-wide count
    // and the Skipped column, which only sums the rows surviving the cluster / BU /
    // Unit Type filters. Left unrelated they read as a contradiction, so the
    // note states its unit (individuals, not assignments) and reconciles the
    // two explicitly rather than leaving the reader to guess at the gap.
    const tenureCount = dataQuality.individualsBelowTenure;
    // ADR-108: Skipped now bundles two reasons, so the tenure note reconciles
    // only ITS people — the excluded-model note does the same for its own.
    const excludedInView = summary.excludedByEngagementModelCount ?? 0;
    const inView = Math.max(0, (summary.skippedCount ?? 0) - excludedInView);
    // Where the rest went. Two distinct fates, and the reader should not have
    // to do the subtraction: some sit in a business unit that produced no row
    // at all (nobody there is eligible, so there is nothing to show them
    // against), the remainder sit in rows the current filters hide.
    const withoutRow = dataQuality.skippedWithoutRowByReason?.[SKIPPED_REASONS.NEW_JOINER]
      ?? dataQuality.individualsBelowTenureWithoutRow
      ?? 0;

    dataQualityNotes.push({
      text: `${tenureCount} ${tenureCount === 1 ? 'individual' : 'individuals'} ${populationLabel} joined ${tenurePhrase} ago, so ${tenureCount === 1 ? 'it is' : 'they are'} not counted`,
      items: reconcileSkipped({
        total: tenureCount, inView, withoutRow, reason: SKIPPED_REASONS.NEW_JOINER,
      }),
    });
  }
  // ADR-108: consultants on an engagement model excluded for the year. Same
  // three-way reconciliation as the tenure note, plus the per-model split,
  // because "41 are excluded" answers nothing until it says which models.
  if (dataQuality.individualsExcludedByEngagementModel > 0) {
    const excludedCount = dataQuality.individualsExcludedByEngagementModel;
    const inView = summary.excludedByEngagementModelCount ?? 0;
    const withoutRow = dataQuality.skippedWithoutRowByReason?.[SKIPPED_REASONS.ENGAGEMENT_MODEL] ?? 0;
    const perModel = (dataQuality.excludedByEngagementModel || [])
      .filter((entry) => entry.count > 0)
      .map((entry) => part(entry.count, excludedCount, `${be(entry.count)} ${entry.name}`));

    dataQualityNotes.push({
      text: `${excludedCount} ${excludedCount === 1 ? 'individual' : 'individuals'} ${populationLabel} ${excludedCount === 1 ? 'is' : 'are'} on an engagement model excluded for ${year}, so ${excludedCount === 1 ? 'it is' : 'they are'} not counted`,
      items: [
        ...(perModel.length > 1 ? [perModel.join(' · ')] : []),
        ...reconcileSkipped({
          total: excludedCount, inView, withoutRow, reason: SKIPPED_REASONS.ENGAGEMENT_MODEL,
        }),
      ],
    });
  }
  // Departed individuals are excluded from every calculation but deliberately
  // not surfaced on this report (product, 2026-08-11) — the API still returns
  // dataQuality.individualsWhoLeft if it is ever wanted back.
  if (dataQuality.individualsWithoutHireDate > 0) {
    const count = dataQuality.individualsWithoutHireDate;
    dataQualityNotes.push({
      text: `${count} allocated ${count === 1 ? 'individual has' : 'individuals have'} no hire date on record, so tenure cannot be checked — excluded`,
    });
  }
  if (dataQuality.assignmentsOutsidePopulation > 0) {
    // Name the rule that dropped the work. A bare total is unactionable —
    // new joiners point at the tenure threshold, no-allocation points at
    // project staffing, and they need different people.
    const byReason = dataQuality.assignmentsOutsideByReason || {};
    const count = dataQuality.assignmentsOutsidePopulation;
    const scope = dataQuality.populationScoped ? ' in this scope' : '';
    dataQualityNotes.push({
      text: `${count} ${count === 1 ? 'assignment is' : 'assignments are'}${scope} not counted in any business unit, because of who owns them`,
      items: [
        [byReason.newJoiner, inflect(
          byReason.newJoiner,
          `belongs to someone who joined ${tenurePhrase} ago`,
          `belong to people who joined ${tenurePhrase} ago`,
        )],
        [byReason.engagementModel, inflect(
          byReason.engagementModel,
          'belongs to someone on an engagement model excluded this year',
          'belong to people on an engagement model excluded this year',
        )],
        [byReason.left, inflect(
          byReason.left,
          'belongs to someone who had already left',
          'belong to people who had already left',
        )],
        [byReason.noAllocation, inflect(
          byReason.noAllocation,
          'belongs to someone with no full-time project allocation this year',
          'belong to people with no full-time project allocation this year',
        )],
        [byReason.noHireDate, inflect(
          byReason.noHireDate,
          'belongs to someone with no hire date on record',
          'belong to people with no hire date on record',
        )],
      ]
        .filter(([n]) => n > 0)
        .map(([n, phrase]) => part(n, count, phrase)),
    });
  }
  // ADR-096: measured against their CURRENT position because position history
  // has no row covering the reference instant. Only ever non-zero for past
  // years — for the current year the two agree by construction — which is
  // exactly where a wrong position quietly changes an old number.
  if (dataQuality.individualsWithoutPositionAtReference > 0) {
    const count = dataQuality.individualsWithoutPositionAtReference;
    dataQualityNotes.push({
      // Singular "they" throughout — the rest of the sentence does not
      // inflect, so the two ternaries that were here had identical branches
      // and only implied a distinction that does not exist.
      text: `${count} ${count === 1 ? 'individual has' : 'individuals have'} no position history for ${year}, so they were measured against the position they hold today`,
    });
  }
  // Only relevant while a unit-type filter is active — that is when untyped
  // BUs are hidden from the grid.
  if (orgUnitTypeCode && dataQuality.serviceUnitsWithoutUnitType > 0) {
    const count = dataQuality.serviceUnitsWithoutUnitType;
    dataQualityNotes.push({
      text: `${count} service ${count === 1 ? 'unit has' : 'units have'} no organizational unit type, so the Unit Type filter hides ${count === 1 ? 'it' : 'them'}`,
    });
  }
  // ADR-095: real work that satisfies no outstanding expectation. "Beyond what
  // their review type expects" was accurate and unreadable — it named the
  // mechanism, not the situation. Say what happened to the assignment (it
  // counts toward nothing) and then why, with a concrete example.
  if (summary.surplusCount > 0) {
    // Fall back to the total when an older payload has no split, rather than
    // reporting zero of each.
    const overTarget = summary.surplusOverTargetCount ?? summary.surplusCount;
    const untyped = summary.surplusUntypedCount ?? 0;
    const total = summary.surplusCount;
    dataQualityNotes.push({
      text: `${total} ${total === 1 ? 'assignment counts' : 'assignments count'} toward no target, so ${total === 1 ? 'it is' : 'they are'} in neither Completed nor Missing`,
      items: [
        [overTarget, inflect(
          overTarget,
          'is an extra assignment of a review type the person had already fulfilled — a second sequential one where the policy expects one',
          'are extra assignments of a review type the person had already fulfilled — a second sequential one where the policy expects one',
        )],
        [untyped, inflect(
          untyped,
          'has no review type yet, because its reviewers have not been set up',
          'have no review type yet, because their reviewers have not been set up',
        )],
      ]
        .filter(([n]) => n > 0)
        .map(([n, phrase]) => part(n, total, phrase)),
    });
  }
  if (summary.suCounts?.withoutPolicy > 0) {
    const count = summary.suCounts.withoutPolicy;
    dataQualityNotes.push({
      text: `${count} business ${count === 1 ? 'unit is' : 'units are'} not measured: their cluster has no Assignment Completion Policy for this year`,
    });
  }

  // ADR-096: a row with position overrides has no single rule, so the tooltip
  // states the general one and then names each exception. Listing them beats a
  // count — "except 2 positions" tells you something differs but not what, and
  // the next question is always which.
  const expectedTooltip = (policy) => {
    if (!policy) {
      return columnHelp.noPolicy;
    }
    const overrides = policy.overrides || [];
    const typeOverrides = policy.typeOverrides || [];
    if (overrides.length === 0 && typeOverrides.length === 0) {
      return policy.description || '';
    }
    const pair = (rule) => {
      const parts = [];
      if (rule.sequentialPerIndividual > 0) parts.push(`${rule.sequentialPerIndividual} sequential`);
      if (rule.parallelPerIndividual > 0) parts.push(`${rule.parallelPerIndividual} parallel`);
      return parts.length > 0 ? parts.join(' + ') : 'nothing expected';
    };
    // Types first, then positions — the order resolution reads them in
    // reverse, so listing them this way shows the general case narrowing.
    const lines = [
      ...typeOverrides.map((rule) => `${rule.orgUnitTypeName || 'Position type'} positions: ${pair(rule)}`),
      ...overrides.map((override) => `${override.positionLabel || 'Position'}: ${pair(override)}`),
    ];
    const general = `${policy.sequentialPerIndividual} sequential + ${policy.parallelPerIndividual} parallel per individual`;
    return `${general}. Except — ${lines.join('; ')}.`;
  };

  // Bars are proportional to the row's EXPECTED count — the same denominator
  // as the % column, so bar lengths compare fairly across BUs.
  const barPct = (count, expected) => (expected > 0 ? (count / expected) * 100 : 0);

  // ── ADR-098: the Open breakdown ───────────────────────────────────────────
  // Lines are ordered closest-to-done first, the same order the server credits
  // capped expectations in, so the first line is the work one action finishes.
  const breakdownLines = (row) => {
    const byStatus = row.openByStatus || {};
    const frozen = row.frozenByStatus || {};
    return OPEN_STATUS_ORDER
      .filter((status) => (byStatus[status] || 0) > 0)
      .map((status) => ({
        status,
        count: byStatus[status] || 0,
        frozen: frozen[status] || 0,
        // Share of THIS row's Open, not of Expected: the panel's own total is
        // the whole it divides.
        pct: row.notCompletedCount > 0 ? (byStatus[status] / row.notCompletedCount) * 100 : 0,
      }));
  };

  // The caption names the population being split, because it is NOT every open
  // assignment in the unit whenever a policy caps the count. Without a policy
  // nothing is capped, and saying "counted toward this unit's target" would
  // invent a target that does not exist (ADR-095 §3).
  const breakdownCaption = (row) => (row.expectedCount === null
    ? `Open work — all ${row.notCompletedCount} open ${inflect(row.notCompletedCount, 'assignment', 'assignments')}, closest to done first`
    : `Open work — the ${row.notCompletedCount} counted toward this unit's target, closest to done first`);

  // The open work the cap left out, named rather than dropped. Omitted
  // entirely when there is nothing to reconcile — an unpolicied row counts
  // every open assignment, so it has no remainder at all.
  const remainderSentence = (row) => {
    const overTarget = row.openSurplusOverTargetCount || 0;
    const untyped = row.openSurplusUntypedCount || 0;
    const total = overTarget + untyped;
    if (total === 0) {
      return null;
    }
    const causes = [];
    if (overTarget > 0) {
      causes.push(`${overTarget} beyond what this unit's policy expects`);
    }
    if (untyped > 0) {
      causes.push(`${untyped} with no review type yet`);
    }
    return `${total} more open ${inflect(total, 'assignment', 'assignments')} ${be(total)} not counted here — ${causes.join(', ')}.`;
  };

  // Column explanations — the API echoes the active policy so the copy
  // states the real thresholds rather than hardcoded ones.
  const columnHelp = getColumnDescriptions(report?.policy);
  // ADR-108: the header stat states its split, then the rule
  const skippedHeaderBreakdown = describeSkippedBreakdown(summary.skippedCount, {
    [SKIPPED_REASONS.NEW_JOINER]: (summary.skippedCount ?? 0) - (summary.excludedByEngagementModelCount ?? 0),
    [SKIPPED_REASONS.ENGAGEMENT_MODEL]: summary.excludedByEngagementModelCount ?? 0,
  });
  const skippedHeaderHelp = skippedHeaderBreakdown
    ? `${skippedHeaderBreakdown}. ${columnHelp.skipped}`
    : columnHelp.skipped;

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate(backTo)}>
            <FaArrowLeft /> Back to {backLabel}
          </BackButton>
          <Title>Assignment Completion</Title>
        </HeaderLeft>
        <HeaderStats>
          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <>
              {/* Same six definitions as the grid headers — one source */}
              <Tooltip title={columnHelp.eligible}>
                <HeaderStat>
                  <div className="top">
                    <span className="label">Measured</span>
                    <span className="value">{summary.eligibleCount}</span>
                  </div>
                </HeaderStat>
              </Tooltip>
              <Tooltip title={skippedHeaderHelp}>
                <HeaderStat>
                  <div className="top">
                    <span className="label">Skipped</span>
                    <span className="value">{summary.skippedCount ?? 0}</span>
                  </div>
                  {/* ADR-108: no longer only tenure */}
                  <span className="sub">Not measured</span>
                </HeaderStat>
              </Tooltip>
              <Tooltip title={columnHelp.expected}>
                <HeaderStat>
                  <div className="top">
                    <span className="label">Expected</span>
                    <span className="value">
                      {summary.expectedCount === null ? '—' : summary.expectedCount}
                    </span>
                  </div>
                </HeaderStat>
              </Tooltip>
              <Tooltip title={columnHelp.notCompleted}>
                <HeaderStat>
                  <div className="top">
                    <span className="label">Open</span>
                    <span className="value">{summary.notCompletedCount}</span>
                  </div>
                  {/* The whole difference between this column and Missing is
                      that the work EXISTS, and "Open" carries that only by
                      implication. The caption says it outright, as Skipped and
                      Missing already do. Deliberately not renamed to
                      "Created": that word is equally true of Completed beside
                      it, and "Allocated" already names the employee↔project
                      allocation this report attributes people by. */}
                  <span className="sub">Created, not finished</span>
                </HeaderStat>
              </Tooltip>
              <Tooltip title={columnHelp.completed}>
                <HeaderStat>
                  <div className="top">
                    <span className="label">Completed</span>
                    <span className="value">{summary.completedCount}</span>
                  </div>
                </HeaderStat>
              </Tooltip>
              <Tooltip title={columnHelp.missing}>
                <HeaderStat>
                  <div className="top">
                    <span className="label">Missing</span>
                    <span className="value">
                      {summary.missingCount === null ? '—' : summary.missingCount}
                    </span>
                  </div>
                  <span className="sub">Expected, never created</span>
                </HeaderStat>
              </Tooltip>
              <Tooltip title={columnHelp.completionRate}>
                <HeaderStat>
                  <div className="top">
                    <span className="label">% Completed</span>
                    <span className="value">
                      {summary.completionRate === null ? '—' : `${summary.completionRate}%`}
                    </span>
                  </div>
                  <span className="sub">Of expected</span>
                </HeaderStat>
              </Tooltip>
            </>
          )}
        </HeaderStats>
      </Header>

      <Content>
        <MainSection>
          {/* Filters */}
          <FiltersBar>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Year</InputLabel>
              <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
                {yearOptions.map((y) => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <ClusterSelector
              value={clusterId}
              onChange={setClusterId}
              label="Cluster"
              showStructureType // ADR-119
              size="small"
              fullWidth={false}
              sx={{ minWidth: 180 }}
            />

            {/* ADR-122 §7 Q1 (2026-09-22): this report family now says
                "Business Unit" throughout — filter, grid and detail page —
                replacing ADR-093 assumption 3's "BU" wording. */}
            <BusinessUnitSelector
              includeInactive // ADR-120: reports keep ended units, marked "Ended <date>"
              value={businessUnitId}
              onChange={setBusinessUnitId}
              label="Business Unit"
              showStructureType // ADR-119
              size="small"
              fullWidth={false}
              sx={{ minWidth: 180 }}
            />

            {/* ADR-093 amendment 2: BU organizational unit type, default Delivery */}
            <OrgUnitTypeSelector
              value={orgUnitTypeCode}
              onChange={setOrgUnitTypeCode}
              returnValue={ORG_UNIT_TYPE_RETURN_TYPES.CODE}
              emptyOptionLabel="All Types"
              size="small"
              fullWidth={false}
              sx={{ minWidth: 210 }}
            />

            {/* Representative (a manager) + the span of their people.
                The chips stay inert until someone is selected. */}
            <EmployeeSearchProvider instanceId="acr-report-representative">
              <EmployeeSearch
                value={representative}
                onChange={handleRepresentativeChange}
                label="Representative"
                placeholder="Search representatives..."
                width={240}
                height={40}
                hideLabel
              />
            </EmployeeSearchProvider>

            <EmployeeReportingScope
              selectedScope={representative ? representativeScope : ''}
              onScopeChange={(scope) => representative && setRepresentativeScope(scope)}
              disabledScopes={[EMPLOYEE_REPORTING_SCOPE.ALL]}
              disabled={!representative}
              labelOverrides={{ [EMPLOYEE_REPORTING_SCOPE.HIERARCHY]: 'Hierarchy' }}
            />

            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Kind</InputLabel>
              <Select
                value={assignmentKind}
                label="Kind"
                onChange={(e) => setAssignmentKind(e.target.value)}
              >
                {ASSIGNMENT_KIND_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Below Target</InputLabel>
              <Select
                value={belowRate}
                label="Below Target"
                onChange={(e) => setBelowRate(e.target.value)}
              >
                {BELOW_RATE_OPTIONS.map((option) => (
                  <MenuItem key={String(option.value)} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FiltersBar>

          {/* Sort control */}
          {!isLoading && rows.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <ResultsCount count={rows.length} singular="business unit" plural="business units" />
              <SortButton onClick={toggleSortDirection}>
                {orderDirection === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />}
                % Completed ({orderDirection === 'asc' ? 'lowest' : 'highest'} first)
              </SortButton>
            </div>
          )}

          {/* BU grid */}
          {isLoading ? (
            <LoadingSpinner />
          ) : rows.length === 0 ? (
            <EmptyState
              title="No Business Units Found"
              description="No business units match the current filters for this period. Try adjusting the period or filters."
            />
          ) : (
            <>
              {/* Names the unit each run of columns counts. Both bands, not
                  just one: the contrast between people and assignments is the
                  point, and a lone band leaves it to be inferred. */}
              <GroupHeader>
                <span className="spacer" />
                <span className="group people">Individuals</span>
                <span className="group assignments">Assignments</span>
              </GroupHeader>
              <GridHeader>
                <span>BU</span>
                <span>Cluster</span>
                {/* Every metric header carries its definition (ADR-014 §10i:
                    all of a vocabulary or none). Bar columns render
                    [count][bar], so their headers sit LEFT above the count;
                    the % column renders [bar][count], so its header stays
                    right. Tooltip clones its child — the grid children stay
                    these spans, so the column layout is unaffected. */}
                <Tooltip title={columnHelp.eligible}>
                  <span className="num help">Measured</span>
                </Tooltip>
                <Tooltip title={columnHelp.skipped}>
                  <span className="num help">Skipped</span>
                </Tooltip>
                <Tooltip title={columnHelp.expected}>
                  <span className="num help">Expected</span>
                </Tooltip>
                <Tooltip title={columnHelp.notCompleted}>
                  <span className="help">Open</span>
                </Tooltip>
                <Tooltip title={columnHelp.completed}>
                  <span className="help">Completed</span>
                </Tooltip>
                <Tooltip title={columnHelp.missing}>
                  <span className="num help">Missing</span>
                </Tooltip>
                <Tooltip title={columnHelp.completionRate}>
                  <span className="num help">% Completed</span>
                </Tooltip>
              </GridHeader>

              {rows.map((row) => {
                const isExpanded = expandedRowIds.has(row.businessUnit.id);
                const lines = isExpanded ? breakdownLines(row) : [];
                const remainder = isExpanded ? remainderSentence(row) : null;
                return (
                <React.Fragment key={row.businessUnit.id}>
                <SuRow>
                  <div className="su-name">
                    {row.nonTerminal && (
                      <Tooltip title="This BU has child BUs but also has individuals allocated to its own projects — its numbers are shown so they are not lost.">
                        <span className="warn"><FaExclamationTriangle /></span>
                      </Tooltip>
                    )}
                    <Tooltip title="See the individuals behind these numbers">
                      <span className="link" onClick={() => openServiceUnit(row)}>
                        {row.businessUnit.name}
                      </span>
                    </Tooltip>
                    {row.expectedCount === null && (
                      <Tooltip title={columnHelp.noPolicy}>
                        <span className="tag">No policy</span>
                      </Tooltip>
                    )}
                  </div>
                  <div className="dc-name">
                    {row.cluster?.name || '—'}
                    {/* With All Types active, rows mix unit types — say which is which */}
                    {!orgUnitTypeCode && ` · ${row.orgUnitType?.name || 'Untyped'}`}
                  </div>
                  <div className="num">{row.eligibleCount}</div>
                  {/* Deliberately outside every calculation — muted so it
                      never reads as part of the arithmetic beside it. */}
                  {row.skippedCount > 0 ? (
                    <Tooltip title={describeSkippedBreakdown(row.skippedCount, row.skippedBreakdown) || columnHelp.skipped}>
                      <div className="num skipped">{row.skippedCount}</div>
                    </Tooltip>
                  ) : (
                    <div className="num muted">0</div>
                  )}
                  {/* ADR-095: null expected = no policy, which is not zero.
                      "—" plus a marker, never a 0 that reads as a failure. */}
                  {row.expectedCount === null ? (
                    <Tooltip title={columnHelp.noPolicy}>
                      <div className="num muted">—</div>
                    </Tooltip>
                  ) : (
                    <Tooltip title={expectedTooltip(row.policy)}>
                      <div className="num">{row.expectedCount}</div>
                    </Tooltip>
                  )}
                  {/* Interior bar columns: the count comes FIRST, touching its
                      own bar — never sandwiched between two bars.
                      Open is the one cell that opens (ADR-098 §5): Completed
                      is a single status, and Missing counts work that does not
                      exist, so neither has anything to expand into. */}
                  {row.notCompletedCount === 0 ? (
                    <BarCell $color={BAR_COLORS.NOT_COMPLETED} $pct={0}>
                      <span className="count muted">0</span>
                      <div className="track"><div className="fill" /></div>
                    </BarCell>
                  ) : (
                    /* No tooltip on this cell. The caret and the underline
                       already say it opens, and a hover bubble here covers the
                       row below — the one place a reader is most likely to be
                       comparing against. What it would have said lives on the
                       Open column header, where every other explanation on
                       this grid lives. The button keeps an aria-label so the
                       affordance survives for anyone not seeing the caret. */
                    <OpenCell
                      $color={BAR_COLORS.NOT_COMPLETED}
                      $pct={barPct(row.notCompletedCount, row.expectedCount)}
                      aria-expanded={isExpanded}
                      aria-controls={`open-breakdown-${row.businessUnit.id}`}
                      aria-label={`${isExpanded ? 'Hide' : 'Show'} how far along ${row.businessUnit.name}'s open work is`}
                      onClick={() => toggleBreakdown(row.businessUnit.id)}
                    >
                      <span className="count">{row.notCompletedCount}</span>
                      <span className="caret"><FaChevronRight /></span>
                      <div className="track"><div className="fill" /></div>
                    </OpenCell>
                  )}
                  <BarCell $color={BAR_COLORS.COMPLETED} $pct={barPct(row.completedCount, row.expectedCount)}>
                    <span className={`count${row.completedCount === 0 ? ' muted' : ''}`}>
                      {row.completedCount}
                    </span>
                    <div className="track"><div className="fill" /></div>
                  </BarCell>
                  <div className={`num${!row.missingCount ? ' muted' : ''}`}>
                    {row.missingCount === null ? '—' : row.missingCount}
                  </div>
                  <RateCell
                    $color={BAR_COLORS.RATE}
                    $pct={row.completionRate === null ? 0 : row.completionRate}
                    $rate={row.completionRate}
                  >
                    <div className="track"><div className="fill" /></div>
                    <span className="count">
                      {row.completionRate === null ? '—' : `${row.completionRate}%`}
                    </span>
                  </RateCell>
                </SuRow>

                {isExpanded && (
                  <BreakdownRow id={`open-breakdown-${row.businessUnit.id}`}>
                    {/* Names the population being split. Without it the panel
                        reads as "every open assignment in this unit", which is
                        not what the Open column counts (ADR-098 §2). */}
                    <div className="caption">{breakdownCaption(row)}</div>
                    <div className="lines">
                      {lines.map((line) => (
                        <React.Fragment key={line.status}>
                          <Tooltip title={getStatusDescription(line.status)}>
                            <span
                              className="chip"
                              style={{
                                backgroundColor: getStatusColor(line.status),
                                color: getStatusTextColor(line.status),
                              }}
                            >
                              {getStatusText(line.status)}
                            </span>
                          </Tooltip>
                          <div className="track">
                            <div className="fill" style={{ width: `${line.pct}%` }} />
                          </div>
                          <div className="value">{line.count}</div>
                          {/* "Left Incomplete" is what the product calls this
                              everywhere else (ADR-014 §10k, AssignmentFrozenBadge).
                              "Frozen" is the word the code uses for the same
                              thing, and shipping it here would have introduced a
                              second name for one state — the mistake this
                              feature keeps refusing to make elsewhere. */}
                          {line.frozen > 0 ? (
                            <Tooltip title={`${getAssignmentTooltipMyPerspective(ASSIGNMENT_BADGES.LEFT_INCOMPLETE)} It was left at: ${getStatusText(line.status)}.`}>
                              <span className="frozen">
                                · {line.frozen} left incomplete
                              </span>
                            </Tooltip>
                          ) : <span />}
                        </React.Fragment>
                      ))}
                    </div>
                    {remainder && <div className="remainder">{remainder}</div>}
                  </BreakdownRow>
                )}
                </React.Fragment>
                );
              })}

              {dataQualityNotes.length > 0 && (
                <DataQualityFooter>
                  <div className="title">Data notes</div>
                  <ul>
                    {dataQualityNotes.map((note) => (
                      <li key={note.text}>
                        {note.text}
                        {note.items?.length > 0 && (
                          <ul>
                            {note.items.map((item) => <li key={item}>{item}</li>)}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                </DataQualityFooter>
              )}
            </>
          )}
        </MainSection>
      </Content>
    </Container>
  );
};

export default AssignmentCompletionReport;
